import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";

import { IGiveaway } from "../../../interfaces";
import { EmitError } from "../../../utils/EmitError";
import useAuth from "../../../hooks/useAuth";
import GiveawaysService from "../../../services/http/services/GiveawaysService";
import useModules from "../../../hooks/useModules";
import InvoicesService from "../../../services/http/services/InvoicesService";
import { convertDataBrParaUS } from "../../../utils/Conversores";
import { EInvoiceStatus } from "../../../enums";
import { useReactToPrint } from "react-to-print";
import TicketsService, { ITicketDistribution } from "../../../services/http/services/TicketsService";
import { styledReportStatsGiveaway } from "./styledReportStatsGiveaway";

export interface IStatsGiveaway {
    total_participants: number;
    total_tickets: number;
    value_total_invoices: number;
    value_total_valid_invoices: number;
    sum_valid_invoices: number;
    sum_invoices: number;
}

export default function useViewGiveaway() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") ?? '1';
    const componentToPrintRef = useRef<HTMLDivElement>(null);

    const [giveawayData, setGiveawayData] = useState<IGiveaway>();
    const [previousGiveawayData, setPreviousGiveawayData] = useState<IGiveaway | null>();
    const [ticketDistribution, setTicketDistribution] = useState<ITicketDistribution[]>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [buttonReportDisabled, setButtonReportDisabled] = useState<boolean>(true);
    const [statsGiveaway, setStatsGiveaway] = useState<IStatsGiveaway>({
        total_participants: 0,
        total_tickets: 0,
        value_total_invoices: 0,
        value_total_valid_invoices: 0,
        sum_valid_invoices: 0,
        sum_invoices: 0,
    });
    const [statsPreviousGiveaway, setStatsPreviousGiveaway] = useState<IStatsGiveaway>({
        total_participants: 0,
        total_tickets: 0,
        value_total_invoices: 0,
        value_total_valid_invoices: 0,
        sum_valid_invoices: 0,
        sum_invoices: 0,
    });

    const { activeCity } = useAuth();
    const { hasRewardModule, hasTaxesModule } = useModules();
    const navigate = useNavigate();

    const handlePrint = useReactToPrint({
        content: () => componentToPrintRef.current,
        documentTitle: 'Relatório do sorteio',
        pageStyle: styledReportStatsGiveaway
    });

    const onChartAnimationEnd = useCallback(() => {
        setButtonReportDisabled(false);
    }, []);

    const getStatsGiveaway = useCallback(async (data: typeof giveawayData | typeof previousGiveawayData) => {
        if (!data || !activeCity) return null;

        const controller = new AbortController();
        const signal = controller.signal;
        const valuesInvoices = await InvoicesService.findValuesInvoices({
            cityId: activeCity.id!,
            startPeriod: convertDataBrParaUS(data.period_beginning),
            endPeriod: convertDataBrParaUS(data.period_ending),
            signal,
        });

        const sum_valid_invoices = valuesInvoices.porStatus.find(
            (item) => item.status === EInvoiceStatus.completed
        )?.total ?? 0;

        const value_total_valid_invoices = valuesInvoices.porStatus.find(
            (item) => item.status === EInvoiceStatus.completed
        )?.sum ?? 0;

        const sum_invoices = valuesInvoices.porStatus.reduce(
            (acc, item) => acc + (item.total || 0),
            0
        );

        return {
            total_tickets: data.total_tickets ?? 0,
            total_participants: data.total_user_participating ?? 0,
            sum_valid_invoices,
            sum_invoices,
            value_total_valid_invoices,
            value_total_invoices: valuesInvoices.total,
        };
    }, [activeCity]);

    const canViewWinners = useMemo(() => {

        const statusThatCanAccess = ["Em verificação", "Finalizado"];

        if (!giveawayData || !statusThatCanAccess.includes(giveawayData.status) || !giveawayData.is_valid) {
            return false;
        }

        return true;
    }, [giveawayData]);




    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);

                if (!activeCity) {
                    return;
                }

                const signal = controller.signal;
                const giveawayId = Number(id);

                const [giveawayData, ticketDistribution, previousGiveawayData] = await Promise.all([
                    GiveawaysService.getGiveawayById({ id: giveawayId, signal }),
                    TicketsService.getTicketDistributionByRaffle({ giveawayId, signal }),
                    GiveawaysService.getPreviousGiveawayByCurrent({ currentGiveawayId: giveawayId, cityId: activeCity.id!, signal }),
                ]);


                if (giveawayData) {
                    const stats = await getStatsGiveaway(giveawayData);
                    setStatsGiveaway(stats!);
                }

                if (previousGiveawayData) {
                    const statsPrev = await getStatsGiveaway(previousGiveawayData);
                    setStatsPreviousGiveaway(statsPrev!);
                }



                setTicketDistribution(ticketDistribution);
                setGiveawayData(giveawayData);
                setPreviousGiveawayData(previousGiveawayData);

                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                setIsLoading(false);

                EmitError(err, true, 'editAppUser_opasdasjpdasd');

                navigate('/sorteios/geral');
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, [id, navigate, getStatsGiveaway, activeCity, hasRewardModule, hasTaxesModule]);

    return {
        isLoading,
        statsGiveaway,
        statsPreviousGiveaway,
        giveawayData,
        previousGiveawayData,
        handlePrint,
        componentToPrintRef,
        ticketDistribution,
        canViewWinners,
        buttonReportDisabled,
        onChartAnimationEnd,
        activeTab
    }
}
